// Nutrition › Repas: week slider, meals planned for the day (photo, « Prévu », « Adapter ce repas » in a drawer), then the
// « Mon cadre » card (coach repères without any computed figure + the studio's guides from the shared catalogue).
import { HoldToConfirm } from "@ubm/ui/components/hold-to-confirm";
import { SplitButton } from "@ubm/ui/components/split-button";
import { Button } from "@ubm/ui/components/button";
import { useState } from "react";
import { Check, ChefHat, ChevronRight, Compass, FileText, Lightbulb, Newspaper, Plus, ShoppingBasket, SlidersHorizontal, Trash2, Video } from "lucide-react";
import { NumberField } from "@ubm/ui/components/number-field";
import { KIND_LABEL, RESOURCES, type Resource } from "./bibliotheque-fixtures";
import { COACH, DAYS, MEALS, MEAL_LABEL, RECIPES, type MealId } from "./nutrition-fixtures";
import { DEMO_TODAY_KEY } from "./demo-context";
import { FilDuJour } from "./fil-du-jour";
import { NutriSheet, Thumb, nutri, recipeHref, recipeOf, useDaySwap, useNutri } from "./nutrition-parts";

const GUIDES = RESOURCES.filter(r => r.topic === "nutrition");
const KIND_ICON = { video: Video, pdf: FileText, article: Newspaper } as const;
let savedDay = DEMO_TODAY_KEY;

type Sheet = { kind: "adapt"; day: string; meal: MealId } | { kind: "pick"; day: string; meal: MealId } | { kind: "cadre" } | { kind: "guide"; r: Resource };

export function NutritionRepas() {
  const s = useNutri();
  const [day, setDayState] = useState(savedDay);
  const setDay = (d: string) => { savedDay = d; setDayState(d); };
  const [sheet, setSheet] = useState<Sheet | null>(null);
  const close = () => setSheet(null);
  const swap = useDaySwap(day, DAYS.map(d => d.key));
  const d = DAYS.find(x => x.key === day) ?? DAYS[0]!;
  const meals = s.plan[day] ?? {};
  const longOf = (key: string) => DAYS.find(x => x.key === key)!.long;

  return <>
    {/* Fil du jour (21/10) : le calendrier du fil remplace la bande de jours et pilote la liste des repas. */}
    <section className="hub-fil" aria-labelledby="ccn-fil"><h2 id="ccn-fil" className="hub-acc-h">Mes repas</h2>
      <FilDuJour domains={["nutrition"]} day={day} onDay={setDay} /></section>

    <div ref={swap} className="ccn-daycontent">
      <div className="ccn-dayhead">
        <h2 className="ccn-h2">{d.long[0]!.toUpperCase() + d.long.slice(1)}</h2>
        {day === DEMO_TODAY_KEY && <span className="ccn-tag">Aujourd'hui</span>}
      </div>
      <ul className="ccn-meals">{MEALS.map(m => {
        const e = meals[m];
        if (!e) return <li key={m}><button type="button" className="ccn-meal-empty" onClick={() => setSheet({ kind: "pick", day, meal: m })}>
          <span className="ccn-over">{MEAL_LABEL[m]}</span><span className="ccn-meal-empty-cta"><Plus size={15} strokeWidth={2.4} /> Ajouter un repas</span>
        </button></li>;
        const isRecipe = "recipe" in e;
        const r = isRecipe ? recipeOf(e.recipe) : null;
        return <li key={m} className="ccn-meal">
          {r ? <a className="ccn-meal-photo" href={recipeHref("repas", r.id)} aria-label={`Voir la recette ${r.name}`} tabIndex={-1}><Thumb photo={r.photo} className="ccn-meal-img" /></a>
            : <span className="ccn-meal-photo"><Thumb className="ccn-meal-img" icon={<Lightbulb size={22} strokeWidth={1.6} />} /></span>}
          <div className="ccn-meal-body">
            <span className="ccn-over">{MEAL_LABEL[m]}</span>
            {r ? <a className="ccn-meal-name" href={recipeHref("repas", r.id)}>{r.name}<ChevronRight size={16} strokeWidth={2} aria-hidden="true" /></a>
              : <span className="ccn-meal-name">{!isRecipe && e.idea}</span>}
            <span className="ccn-meal-meta">
              <span className="ccn-badge"><Check size={11} strokeWidth={3} /> {r ? "Prévu" : "Idée prévue"}</span>
              {r && isRecipe && <span>{e.portions} portion{e.portions > 1 ? "s" : ""} · {r.minutes} min</span>}
            </span>
            <SplitButton variant="secondary" size="sm" label="Adapter ce repas" icon={<SlidersHorizontal size={15} strokeWidth={2} />} onClick={() => setSheet({ kind: "adapt", day, meal: m })}
              actions={[{ label: "Changer de recette", icon: <ChefHat size={15} />, onSelect: () => setSheet({ kind: "adapt", day, meal: m }) },
                ...(r && isRecipe ? [{ label: s.extras[r.id] ? "Déjà dans les courses" : "Ajouter aux courses", icon: <ShoppingBasket size={15} />, disabled: !!s.extras[r.id],
                  onSelect: () => { nutri.set({ extras: { ...s.extras, [r.id]: e.portions } }); nutri.toast(`${r.ingredients.length} ingrédients ajoutés aux courses`); } }] : [])]} />
          </div>
        </li>;
      })}</ul>
    </div>

    <button type="button" className="ccn-cadre" onClick={() => setSheet({ kind: "cadre" })}>
      <span className="ccn-cadre-ic" aria-hidden="true"><Compass size={20} strokeWidth={1.8} /></span>
      <span className="ccn-cadre-text"><span className="ccn-over">Mon cadre</span><b>{COACH?.goal ?? "Les bases de l'alimentation"}</b><small>Mes repères et les guides du studio</small></span>
      <ChevronRight size={18} strokeWidth={2} aria-hidden="true" />
    </button>

    {sheet?.kind === "adapt" && (() => { const e = s.plan[sheet.day]?.[sheet.meal]; if (!e) return null; const cur = "recipe" in e ? e : null;
      return <NutriSheet title="Adapter ce repas" onClose={close}>
        <p className="ccn-sheet-sub">{MEAL_LABEL[sheet.meal]} · {longOf(sheet.day)}</p>
        {cur && <NumberField label="Portions" value={cur.portions} min={1} max={8} locale="fr-FR" size="sm" onValueChange={v => nutri.setEntry(sheet.day, sheet.meal, { recipe: cur.recipe, portions: v })} />}
        <p className="ccn-sheet-label">Changer de recette</p>
        <ul className="ccn-pick">{RECIPES.map(r => <li key={r.id}><button type="button" className="ccn-pick-row" data-on={cur?.recipe === r.id || undefined}
          onClick={() => { nutri.setEntry(sheet.day, sheet.meal, { recipe: r.id, portions: cur?.portions ?? 1 }); close(); nutri.toast(`${MEAL_LABEL[sheet.meal]} : ${r.name}`); }}>
          <Thumb photo={r.photo} className="ccn-pick-img" /><span><b>{r.name}</b><small>{r.minutes} min</small></span>{cur?.recipe === r.id && <Check size={16} strokeWidth={2.6} />}
        </button></li>)}</ul>
        <HoldToConfirm className="ccn-hold" label="Maintenir pour retirer ce repas" confirmedLabel="Repas retiré" tone="danger" icon={<Trash2 strokeWidth={1.75} />} onConfirm={() => { window.setTimeout(() => { nutri.setEntry(sheet.day, sheet.meal, null); close(); nutri.toast("Repas retiré"); }, 600); }} />
      </NutriSheet>; })()}

    {sheet?.kind === "pick" && <NutriSheet title="Ajouter un repas" onClose={close}>
      <p className="ccn-sheet-sub">{MEAL_LABEL[sheet.meal]} · {longOf(sheet.day)}</p>
      <ul className="ccn-pick">{RECIPES.map(r => <li key={r.id}><button type="button" className="ccn-pick-row"
        onClick={() => { nutri.setEntry(sheet.day, sheet.meal, { recipe: r.id, portions: 1 }); close(); nutri.toast(`${r.name} ajouté`); }}>
        <Thumb photo={r.photo} className="ccn-pick-img" /><span><b>{r.name}</b><small>{r.minutes} min · 1 portion</small></span><Plus size={16} strokeWidth={2.4} />
      </button></li>)}</ul>
    </NutriSheet>}

    {sheet?.kind === "cadre" && <NutriSheet title="Mon cadre" onClose={close}>
      {COACH ? <>
        {COACH.goal && <div className="ccn-kv"><span>Objectif</span><b>{COACH.goal}</b></div>}
        {COACH.priority && <div className="ccn-kv"><span>Priorité de ton coach</span><b>{COACH.priority}</b></div>}
        <p className="ccn-sheet-label">Mes repères</p>
        <ul className="ccn-reperes">{COACH.reperes.map(r => <li key={r.title}><Check size={15} strokeWidth={2.6} /><span><b>{r.title}</b><small>{r.detail}</small></span></li>)}</ul>
      </> : <p className="cc-home-p">Ton coach n'a pas encore partagé de repères.</p>}
      <p className="ccn-note">Aucune estimation n'est calculée ici : la méthode d'estimation n'est pas validée.</p>
      {GUIDES.length > 0 && <>
        <p className="ccn-sheet-label">Les guides du studio</p>
        <ul className="ccn-pick">{GUIDES.map(g => { const Icon = KIND_ICON[g.kind]; return <li key={g.id}><button type="button" className="ccn-pick-row" onClick={() => setSheet({ kind: "guide", r: g })}>
          <Thumb photo={g.photo} className="ccn-pick-img" icon={<Icon size={18} strokeWidth={1.8} />} /><span><b>{g.title}</b><small>{KIND_LABEL[g.kind]}{g.duration ? ` · ${g.duration}` : ""}</small></span><ChevronRight size={16} strokeWidth={2} />
        </button></li>; })}</ul>
      </>}
    </NutriSheet>}

    {sheet?.kind === "guide" && <NutriSheet title={sheet.r.title} onClose={() => setSheet({ kind: "cadre" })}>
      <p className="ccn-sheet-sub">{KIND_LABEL[sheet.r.kind]}{sheet.r.duration ? ` · ${sheet.r.duration}` : ""}</p>
      <div className="cc-biblio-article">{sheet.r.body?.map(x => <p key={x} className="cc-home-p">{x}</p>)}</div>
    </NutriSheet>}
  </>;
}
